// #355: the rows of a program that never progress, read by the editor's own
// rule reader (recognizeRule) on the program's Draft. Pure.
import type { Program } from "./schemas/program.js";
import { programToDraft } from "./programEditRead.js";
import { isEffortAxis } from "./programProgression.js";
import { recognizeRule } from "./programProgressionRecognize.js";
import { roundTypeAt } from "./programRoundTypes.js";

export type StaticRow = {
  sessionIdx: number;
  slotId: string;
  exerciseId: string;
  /** The rounds compared, deloads aside. */
  rounds: number;
  /** The row's axis is RPE or RIR: the load is the athlete's pick, so it can
   *  still rise while the prescription stays the same. */
  loadByEffort: boolean;
};

/** A row never progresses when its rule, with no exception outside the
 *  deloads, steps neither reps nor value and has no threshold. Wider than
 *  the rule NAME
 *  «constant»: a deload or test effect («tetrad»), a fixed per-set shape
 *  («pyramid», «topBackoff», «perSet»), «history» and «timed» rows win the
 *  name and still never step. A row read «by hand» is not judged, nor one
 *  with fewer than two progressing rounds. The Draft reads the first
 *  variant only, so an alternating slot is one row. */
export const staticRows = (program: Program): StaticRow[] => {
  const draft = programToDraft(program, "kg");
  const types = Array.from({ length: draft.cycleWeeks }, (_, w) => roundTypeAt(draft, w));
  const out: StaticRow[] = [];
  draft.sessions.forEach((session, sessionIdx) => {
    for (const slot of session.slots) {
      const rounds = slot.inactiveWeeks.filter(
        (off, w) => !off && types[w] !== "deload",
      ).length;
      if (rounds < 2) continue;
      const rec = recognizeRule(slot, types);
      if (rec.kind !== "rule") continue;
      if (rec.exceptions.some((e) => types[e.week] !== "deload")) continue;
      const { rule } = rec;
      const flat =
        !rule.threshold &&
        rule.reps.kind === "constant" &&
        (rule.value === null || rule.value.kind === "constant");
      if (!flat) continue;
      out.push({
        sessionIdx,
        slotId: slot.id,
        exerciseId: slot.exerciseId,
        rounds,
        loadByEffort: isEffortAxis(rule.axis),
      });
    }
  });
  return out;
};
